import { i18nNsFactory } from '@atlas-frontend/i18n';
import { Space } from 'antd';
import { FC, useCallback, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import { Select } from '../../../common/components';
import {
  FormField,
  FormFieldProps,
  FormInput,
  useGetIdFromName,
} from '../../../common/form';
import {
  decodeStringKey,
  encodeStringKey,
  getFromMapSafely,
} from '../../../common/utils';
import { AlgorithmMappingQueryDtoType } from '../mappings/schema/types';
import { getAlgorithmSourceShortLabel } from '../utils';
import translations from './AlgorithmJoinItemDbColumnField.translations.json';
import { AlgorithmJoinsFormStore } from './AlgorithmJoinsFormStore';
import { AlgorithmJoinsValidFormDataType } from './schema/types';
import { useUsedAlgorithmSourceIds } from './useUsedAlgorithmSourceIds';
import {
  changeAlgorithmJoinItemFormDataForDb,
  useWatchAlgorithmJoinValue,
} from './utils';

const { addTranslations, useTranslation } = i18nNsFactory(
  'algorithm-join-item-db-column-field',
);
addTranslations(translations);

const MAPPING_TYPE = 'mapping';
const SOURCE_TYPE = 'source';

export interface AlgorithmJoinItemDbColumnFieldProps
  extends Omit<FormFieldProps, 'name'> {
  joinIndex: number;
  joinItemIndex: number;
  side: 'source' | 'target';
  mappings: AlgorithmMappingQueryDtoType[];
}
export const AlgorithmJoinItemDbColumnField: FC<
  AlgorithmJoinItemDbColumnFieldProps
> = (props) => {
  const { t } = useTranslation();
  const { joinIndex, joinItemIndex, side, mappings, ...rest } = props;

  const joinName = `joins.${joinIndex}` as const;
  const joinItemName =
    `${joinName}.algorithmJoinItems.${joinItemIndex}` as const;

  const { getValues, setValue } =
    useFormContext<AlgorithmJoinsValidFormDataType>();
  const [algorithmSourcesByIds] = AlgorithmJoinsFormStore.useValue(
    'algorithmSourcesByIds',
  );

  const mappingAlias = useWatchAlgorithmJoinValue(
    `${joinItemName}.algorithmMappingAlias`,
  );
  const joinItemAlgorithmSourceId = useWatchAlgorithmJoinValue(
    `${joinItemName}.algorithmSourceId`,
  );
  const joinAlgorithmSourceId = useWatchAlgorithmJoinValue(
    `${joinName}.algorithmSourceId`,
  );
  const variant = useWatchAlgorithmJoinValue(`${joinItemName}._variant`);
  const isMappingSelected =
    side === 'source'
      ? variant?.startsWith('MAPPING_')
      : variant?.endsWith('_MAPPING');

  const usedAlgorithmSourceIds = useUsedAlgorithmSourceIds({
    untilIndex: joinIndex,
  });

  const options = useMemo(() => {
    const options: {
      label: string;
      options: {
        label: string;
        value: `${'mapping' | 'source'}~${string}`;
      }[];
    }[] = [];

    if (mappings.length) {
      options.push({
        label: t('mappings'),
        options: mappings.map((m) => ({
          label: m.alias,
          value: encodeStringKey(MAPPING_TYPE, m.alias),
        })),
      });
    }

    const sourceIds =
      side === 'source'
        ? usedAlgorithmSourceIds
        : joinAlgorithmSourceId
          ? [joinAlgorithmSourceId]
          : [];

    if (sourceIds.length) {
      options.push({
        label: t('sources'),
        options: sourceIds.map((id) => ({
          label: getAlgorithmSourceShortLabel(
            getFromMapSafely(algorithmSourcesByIds, id),
          ),
          value: encodeStringKey(SOURCE_TYPE, String(id)),
        })),
      });
    }

    return options;
  }, [
    t,
    mappings,
    side,
    isMappingSelected,
    usedAlgorithmSourceIds,
    joinAlgorithmSourceId,
    algorithmSourcesByIds,
  ]);

  const selectValue = useMemo(
    () =>
      isMappingSelected
        ? mappingAlias
          ? encodeStringKey(MAPPING_TYPE, mappingAlias)
          : undefined
        : side === 'source'
          ? joinItemAlgorithmSourceId
            ? encodeStringKey(SOURCE_TYPE, String(joinItemAlgorithmSourceId))
            : undefined
          : joinAlgorithmSourceId
            ? encodeStringKey(SOURCE_TYPE, String(joinAlgorithmSourceId))
            : undefined,
    [
      isMappingSelected,
      joinAlgorithmSourceId,
      joinItemAlgorithmSourceId,
      mappingAlias,
      side,
    ],
  );

  const handleChange = useCallback(
    (value: string | null) => {
      const [type, payload] = value ? decodeStringKey(value) : [];
      setValue(
        joinItemName,
        changeAlgorithmJoinItemFormDataForDb({
          currentItem: getValues(joinItemName),
          side,
          ...(!value
            ? { algorithmSourceId: null! }
            : type === MAPPING_TYPE
              ? { algorithmMappingAlias: payload }
              : { algorithmSourceId: Number(payload) }),
        }),
        { shouldDirty: true },
      );
    },
    [getValues, setValue, joinItemName, side],
  );

  useEffect(() => {
    if (side === 'source') {
      const value = getValues(`${joinItemName}.algorithmSourceId`);
      if (value && !usedAlgorithmSourceIds.includes(value)) {
        setValue(`${joinItemName}.algorithmSourceId`, null!);
      }
    }
  }, [getValues, setValue, joinItemName, side, usedAlgorithmSourceIds]);

  const name = isMappingSelected
    ? `${joinItemName}.algorithmMappingAlias`
    : `${joinItemName}.${side}Dimension`;

  const getIdFromName = useGetIdFromName();

  const renderSelect = (width: string, nameForId: string) => (
    <Select
      id={getIdFromName(nameForId)}
      options={options}
      value={selectValue}
      onChange={handleChange}
      style={{ width }}
      styles={{
        popup: { root: { width: 'max-content' } },
      }}
      allowClear
    />
  );

  return (
    <FormField name={name} {...rest}>
      {isMappingSelected ? (
        renderSelect('100%', name)
      ) : (
        <CompactWrapper>
          {renderSelect(
            'max-content',
            side === 'source'
              ? `${joinItemName}.algorithmSourceId`
              : `${joinName}.algorithmSourceId`,
          )}
          <FormInput name={name} />
        </CompactWrapper>
      )}
    </FormField>
  );
};

const CompactWrapper = styled(Space.Compact)`
  width: 100%;
`;
